import React, { useState, Fragment, Component } from 'react';
import { Building2, Users, FolderKanban, Plug, Bot, LayoutGrid, ChevronRight, ChevronLeft, Check, Sparkles, ArrowRight, Globe, Zap, GitBranch, MessageSquare, BookOpen, Bug, Map, Plus, X, Pencil, Rocket } from 'lucide-react';
import { Button } from '../ui/Button';
import { useBrandConfig, BoardType } from '../../hooks/useBrandConfig';
interface SetupWizardProps {
  onComplete: () => void;
  onCancel?: () => void;
  mode?: 'setup' | 'boards';
  startStep?: number;
}
const STEPS = [{
  id: 'company',
  label: 'Empresa',
  icon: Building2
}, {
  id: 'team',
  label: 'Equipo',
  icon: Users
}, {
  id: 'project',
  label: 'Proyecto',
  icon: FolderKanban
}, {
  id: 'integrations',
  label: 'Integraciones',
  icon: Plug
}, {
  id: 'agent',
  label: 'Agente IA',
  icon: Bot
}, {
  id: 'boards',
  label: 'Tableros',
  icon: LayoutGrid
}, {
  id: 'review',
  label: 'Resumen',
  icon: Rocket
}];
export function SetupWizard({
  onComplete,
  onCancel,
  mode = 'setup',
  startStep = 0
}: SetupWizardProps) {
  const {
    brand,
    updateBrand
  } = useBrandConfig();
  const [currentStep, setCurrentStep] = useState(mode === 'boards' ? 5 : startStep);
  const [customBoardName, setCustomBoardName] = useState('');
  const [customBoardDesc, setCustomBoardDesc] = useState('');
  const [showAddBoard, setShowAddBoard] = useState(false);
  // Local form state (committed on next/complete)
  const [form, setForm] = useState({
    company: brand.company,
    name: brand.name,
    logoInitial: brand.logoInitial,
    tagline: brand.tagline,
    industry: brand.industry,
    teamName: brand.teamName,
    teamSize: brand.teamSize,
    projectKey: brand.projectKey,
    methodology: brand.methodology,
    sprintDuration: brand.sprintDuration,
    jiraBaseUrl: brand.jiraBaseUrl,
    jiraConnected: brand.jiraConnected,
    confluenceConnected: brand.confluenceConnected,
    slackConnected: brand.slackConnected,
    githubConnected: brand.githubConnected,
    agentTone: brand.agentTone,
    agentLanguage: brand.agentLanguage,
    boardTypes: [...brand.boardTypes]
  });
  const updateForm = (updates: Partial<typeof form>) => setForm(prev => ({
    ...prev,
    ...updates
  }));
  const handleNext = () => {
    if (currentStep < STEPS.length - 1) setCurrentStep(prev => prev + 1);
  };
  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(prev => prev - 1);
  };
  const handleComplete = () => {
    updateBrand({
      ...form,
      setupCompleted: true,
      footerText: `Powered by ${form.name}`
    });
    onComplete();
  };
  const toggleBoard = (id: string) => {
    updateForm({
      boardTypes: form.boardTypes.map(b => b.id === id ? {
        ...b,
        enabled: !b.enabled
      } : b)
    });
  };
  const addCustomBoard = () => {
    if (!customBoardName.trim()) return;
    const newBoard: BoardType = {
      id: `custom-${Date.now()}`,
      name: customBoardName,
      type: 'custom',
      enabled: true,
      icon: '⚡',
      description: customBoardDesc || 'Tablero personalizado'
    };
    updateForm({
      boardTypes: [...form.boardTypes, newBoard]
    });
    setCustomBoardName('');
    setCustomBoardDesc('');
    setShowAddBoard(false);
  };
  const removeBoard = (id: string) => {
    updateForm({
      boardTypes: form.boardTypes.filter(b => b.id !== id)
    });
  };
  const activeSteps = mode === 'boards' ? STEPS.slice(5, 7) : STEPS;
  const displayStepIndex = mode === 'boards' ? currentStep - 5 : currentStep;
  return <div className="fixed inset-0 z-[100] bg-slate-950 flex flex-col overflow-hidden" data-id="element-6359">
      {/* Header */}
      <div className="h-16 bg-slate-900 border-b border-slate-800 flex items-center justify-between px-8 shrink-0" data-id="element-6360">
        <div className="flex items-center gap-3" data-id="element-6361">
          <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center text-white font-bold shadow-lg shadow-indigo-900/30" data-id="element-6362">
            {form.logoInitial || 'A'}
          </div>
          <h1 className="text-lg font-bold text-slate-100" data-id="element-6363">
            {mode === 'boards' ? 'Gestionar Tableros' : 'Setup Wizard'}
          </h1>
        </div>
        {onCancel && <button onClick={onCancel} className="text-slate-500 hover:text-slate-300 transition-colors p-2 rounded-lg hover:bg-slate-800" data-id="element-6364">
            <X size={20} data-id="element-6365" />
          </button>}
      </div>

      {/* Progress Bar */}
      <div className="bg-slate-900/50 border-b border-slate-800 px-8 py-4 shrink-0" data-id="element-6366">
        <div className="max-w-3xl mx-auto" data-id="element-6367">
          <div className="flex items-center justify-between" data-id="element-6368">
            {(mode === 'boards' ? STEPS.slice(5) : STEPS).map((step, idx) => {
            const stepIdx = mode === 'boards' ? idx + 5 : idx;
            const isActive = currentStep === stepIdx;
            const isCompleted = currentStep > stepIdx;
            const Icon = step.icon;
            return <Fragment key={step.id} data-id="element-6369">
                  <div className="flex flex-col items-center gap-1.5" data-id="element-6370">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${isCompleted ? 'bg-emerald-600 text-white' : isActive ? 'bg-indigo-600 text-white ring-4 ring-indigo-900/50' : 'bg-slate-800 text-slate-500 border border-slate-700'}`} data-id="element-6371">
                      {isCompleted ? <Check size={18} data-id="element-6372" /> : <Icon size={18} data-id="element-6373" />}
                    </div>
                    <span className={`text-xs font-medium ${isActive ? 'text-indigo-400' : isCompleted ? 'text-emerald-400' : 'text-slate-500'}`} data-id="element-6374">
                      {step.label}
                    </span>
                  </div>
                  {idx < (mode === 'boards' ? 1 : STEPS.length - 1) && <div className={`flex-1 h-0.5 mx-2 rounded ${isCompleted ? 'bg-emerald-600' : 'bg-slate-800'}`} data-id="element-6375" />}
                </Fragment>;
          })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-8" data-id="element-6376">
        <div className="max-w-2xl mx-auto" data-id="element-6377">
          {/* STEP 0: Company */}
          {currentStep === 0 && <div className="space-y-8 animate-in fade-in slide-in-from-right-10 duration-300" data-id="element-6378">
              <div data-id="element-6379">
                <h2 className="text-2xl font-bold text-slate-100 mb-2" data-id="element-6380">
                  🏢 Información de la Empresa
                </h2>
                <p className="text-slate-400" data-id="element-6381">
                  Configuremos la identidad de tu plataforma.
                </p>
              </div>
              <div className="space-y-5" data-id="element-6382">
                <FormField label="Nombre de la Empresa" value={form.company} onChange={v => updateForm({
              company: v
            })} placeholder="Ej. Workium AI" data-id="element-6383" />

                <FormField label="Nombre del Producto" value={form.name} onChange={v => updateForm({
              name: v
            })} placeholder="Ej. Workium AI" hint="Este nombre aparecerá en toda la interfaz" data-id="element-6384" />

                <div className="grid grid-cols-2 gap-4" data-id="element-6385">
                  <FormField label="Inicial del Logo" value={form.logoInitial} onChange={v => updateForm({
                logoInitial: v.slice(0, 2)
              })} placeholder="A" maxLength={2} data-id="element-6386" />

                  <div data-id="element-6387">
                    <label className="block text-sm font-medium text-slate-300 mb-1.5" data-id="element-6388">
                      Industria
                    </label>
                    <select value={form.industry} onChange={e => updateForm({
                  industry: e.target.value
                })} className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-6389">
                      {['Technology', 'Finance', 'Healthcare', 'E-commerce', 'Education', 'Media', 'Consulting', 'Other'].map(i => <option key={i} value={i} data-id="element-6390">
                          {i}
                        </option>)}
                    </select>
                  </div>
                </div>
                <FormField label="Tagline" value={form.tagline} onChange={v => updateForm({
              tagline: v
            })} placeholder="Tu Scrum Master Virtual Inteligente" data-id="element-6391" />

                {/* Live Preview */}
                <div className="bg-slate-900 rounded-xl p-5 border border-slate-800 mt-6" data-id="element-6392">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 font-semibold" data-id="element-6393">
                    Vista Previa
                  </p>
                  <div className="flex items-center gap-4" data-id="element-6394">
                    <div className="w-14 h-14 bg-indigo-600 rounded-xl flex items-center justify-center text-white font-bold text-2xl shadow-lg shadow-indigo-900/30" data-id="element-6395">
                      {form.logoInitial || '?'}
                    </div>
                    <div data-id="element-6396">
                      <h3 className="text-xl font-bold text-slate-100" data-id="element-6397">
                        {form.name || 'Tu Producto'}
                      </h3>
                      <p className="text-sm text-slate-400" data-id="element-6398">
                        {form.tagline || 'Tagline aquí'}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5" data-id="element-6399">
                        {form.company || 'Empresa'} · {form.industry}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>}

          {/* STEP 1: Team */}
          {currentStep === 1 && <div className="space-y-8 animate-in fade-in slide-in-from-right-10 duration-300" data-id="element-6400">
              <div data-id="element-6401">
                <h2 className="text-2xl font-bold text-slate-100 mb-2" data-id="element-6402">
                  👥 Configuración del Equipo
                </h2>
                <p className="text-slate-400" data-id="element-6403">
                  Cuéntanos sobre tu equipo para personalizar la experiencia.
                </p>
              </div>
              <div className="space-y-5" data-id="element-6404">
                <FormField label="Nombre del Equipo" value={form.teamName} onChange={v => updateForm({
              teamName: v
            })} placeholder="Ej. Team Alpha" data-id="element-6405" />

                <div data-id="element-6406">
                  <label className="block text-sm font-medium text-slate-300 mb-3" data-id="element-6407">
                    Tamaño del Equipo
                  </label>
                  <div className="grid grid-cols-4 gap-3" data-id="element-6408">
                    {[{
                  value: '1-5',
                  label: '1-5',
                  desc: 'Startup'
                }, {
                  value: '5-10',
                  label: '5-10',
                  desc: 'Small'
                }, {
                  value: '10-25',
                  label: '10-25',
                  desc: 'Medium'
                }, {
                  value: '25+',
                  label: '25+',
                  desc: 'Large'
                }].map(({
                  value,
                  label,
                  desc
                }) => <button key={value} onClick={() => updateForm({
                  teamSize: value
                })} className={`p-4 rounded-xl border text-center transition-all ${form.teamSize === value ? 'bg-indigo-900/30 border-indigo-500 text-indigo-300' : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700'}`} data-id="element-6409">
                        <span className="text-xl font-bold block" data-id="element-6410">{label}</span>
                        <span className="text-xs mt-1 block opacity-70" data-id="element-6411">
                          {desc}
                        </span>
                      </button>)}
                  </div>
                </div>
              </div>
            </div>}

          {/* STEP 2: Project */}
          {currentStep === 2 && <div className="space-y-8 animate-in fade-in slide-in-from-right-10 duration-300" data-id="element-6412">
              <div data-id="element-6413">
                <h2 className="text-2xl font-bold text-slate-100 mb-2" data-id="element-6414">
                  📁 Configuración del Proyecto
                </h2>
                <p className="text-slate-400" data-id="element-6415">Define cómo trabaja tu equipo.</p>
              </div>
              <div className="space-y-5" data-id="element-6416">
                <FormField label="Clave del Proyecto" value={form.projectKey} onChange={v => updateForm({
              projectKey: v.toUpperCase().slice(0, 5)
            })} placeholder="VSM" hint="Prefijo para los issues (ej. VSM-101)" maxLength={5} data-id="element-6417" />

                <div data-id="element-6418">
                  <label className="block text-sm font-medium text-slate-300 mb-3" data-id="element-6419">
                    Metodología
                  </label>
                  <div className="grid grid-cols-2 gap-3" data-id="element-6420">
                    {[{
                  value: 'scrum' as const,
                  label: 'Scrum',
                  icon: '🏃',
                  desc: 'Sprints, ceremonies, story points'
                }, {
                  value: 'kanban' as const,
                  label: 'Kanban',
                  icon: '📋',
                  desc: 'Flujo continuo, WIP limits'
                }, {
                  value: 'safe' as const,
                  label: 'SAFe',
                  icon: '🏗️',
                  desc: 'Scaled Agile Framework'
                }, {
                  value: 'custom' as const,
                  label: 'Personalizado',
                  icon: '⚙️',
                  desc: 'Configura tu propio flujo'
                }].map(({
                  value,
                  label,
                  icon,
                  desc
                }) => <button key={value} onClick={() => updateForm({
                  methodology: value
                })} className={`p-4 rounded-xl border text-left transition-all ${form.methodology === value ? 'bg-indigo-900/30 border-indigo-500' : 'bg-slate-900 border-slate-800 hover:border-slate-700'}`} data-id="element-6421">
                        <div className="flex items-center gap-2 mb-1" data-id="element-6422">
                          <span className="text-lg" data-id="element-6423">{icon}</span>
                          <span className={`font-semibold ${form.methodology === value ? 'text-indigo-300' : 'text-slate-200'}`} data-id="element-6424">
                            {label}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500" data-id="element-6425">{desc}</p>
                      </button>)}
                  </div>
                </div>

                {(form.methodology === 'scrum' || form.methodology === 'safe') && <div data-id="element-6426">
                    <label className="block text-sm font-medium text-slate-300 mb-3" data-id="element-6427">
                      Duración del Sprint
                    </label>
                    <div className="flex gap-3" data-id="element-6428">
                      {[1, 2, 3, 4].map(w => <button key={w} onClick={() => updateForm({
                  sprintDuration: w
                })} className={`flex-1 py-3 rounded-xl border text-center transition-all ${form.sprintDuration === w ? 'bg-indigo-900/30 border-indigo-500 text-indigo-300' : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700'}`} data-id="element-6429">
                          <span className="font-bold" data-id="element-6430">{w}</span>
                          <span className="text-xs block mt-0.5" data-id="element-6431">
                            {w === 1 ? 'semana' : 'semanas'}
                          </span>
                        </button>)}
                    </div>
                  </div>}
              </div>
            </div>}

          {/* STEP 3: Integrations */}
          {currentStep === 3 && <div className="space-y-8 animate-in fade-in slide-in-from-right-10 duration-300" data-id="element-6432">
              <div data-id="element-6433">
                <h2 className="text-2xl font-bold text-slate-100 mb-2" data-id="element-6434">
                  🔌 Integraciones
                </h2>
                <p className="text-slate-400" data-id="element-6435">
                  Conecta tus herramientas favoritas.
                </p>
              </div>
              <div className="space-y-4" data-id="element-6436">
                <FormField label="Jira Base URL" value={form.jiraBaseUrl} onChange={v => updateForm({
              jiraBaseUrl: v
            })} placeholder="https://tu-empresa.atlassian.net/browse" data-id="element-6437" />

                {[{
              key: 'jiraConnected' as const,
              name: 'Jira Software',
              desc: 'Issues, sprints, boards',
              icon: '🔵',
              color: 'text-blue-400'
            }, {
              key: 'confluenceConnected' as const,
              name: 'Confluence',
              desc: 'Documentación, decision logs',
              icon: '📘',
              color: 'text-blue-400'
            }, {
              key: 'slackConnected' as const,
              name: 'Slack',
              desc: 'Notificaciones, daily standup',
              icon: '💬',
              color: 'text-purple-400'
            }, {
              key: 'githubConnected' as const,
              name: 'GitHub',
              desc: 'Pull requests, CI/CD, code review',
              icon: '🐙',
              color: 'text-slate-300'
            }].map(({
              key,
              name,
              desc,
              icon
            }) => <div key={key} className={`flex items-center justify-between p-4 rounded-xl border transition-all ${form[key] ? 'bg-slate-900 border-emerald-800/50' : 'bg-slate-900 border-slate-800'}`} data-id="element-6438">
                    <div className="flex items-center gap-3" data-id="element-6439">
                      <span className="text-2xl" data-id="element-6440">{icon}</span>
                      <div data-id="element-6441">
                        <p className="text-sm font-semibold text-slate-200" data-id="element-6442">
                          {name}
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-6443">{desc}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-6444">
                      {form[key] && <span className="text-xs text-emerald-400 font-medium" data-id="element-6445">
                          Conectado
                        </span>}
                      <button onClick={() => updateForm({
                  [key]: !form[key]
                } as any)} className={`relative w-12 h-6 rounded-full transition-colors ${form[key] ? 'bg-emerald-600' : 'bg-slate-700'}`} data-id="element-6446">
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form[key] ? 'translate-x-6' : 'translate-x-0'}`} data-id="element-6447" />
                      </button>
                    </div>
                  </div>)}
              </div>
            </div>}

          {/* STEP 4: Agent */}
          {currentStep === 4 && <div className="space-y-8 animate-in fade-in slide-in-from-right-10 duration-300" data-id="element-6448">
              <div data-id="element-6449">
                <h2 className="text-2xl font-bold text-slate-100 mb-2" data-id="element-6450">
                  🤖 Personalidad del Agente
                </h2>
                <p className="text-slate-400" data-id="element-6451">
                  Define cómo se comporta tu asistente IA.
                </p>
              </div>
              <div className="space-y-5" data-id="element-6452">
                <div data-id="element-6453">
                  <label className="block text-sm font-medium text-slate-300 mb-3" data-id="element-6454">
                    Tono de Conversación
                  </label>
                  <div className="grid grid-cols-2 gap-3" data-id="element-6455">
                    {[{
                  value: 'Professional',
                  emoji: '👔',
                  desc: 'Formal, directo, orientado a datos'
                }, {
                  value: 'Friendly',
                  emoji: '😊',
                  desc: 'Cercano, motivador, empático'
                }, {
                  value: 'Enthusiastic',
                  emoji: '🚀',
                  desc: 'Energético, proactivo, celebra logros'
                }, {
                  value: 'Strict',
                  emoji: '📋',
                  desc: 'Riguroso, enfocado en deadlines'
                }].map(({
                  value,
                  emoji,
                  desc
                }) => <button key={value} onClick={() => updateForm({
                  agentTone: value
                })} className={`p-4 rounded-xl border text-left transition-all ${form.agentTone === value ? 'bg-indigo-900/30 border-indigo-500' : 'bg-slate-900 border-slate-800 hover:border-slate-700'}`} data-id="element-6456">
                        <div className="flex items-center gap-2 mb-1" data-id="element-6457">
                          <span className="text-lg" data-id="element-6458">{emoji}</span>
                          <span className={`font-semibold ${form.agentTone === value ? 'text-indigo-300' : 'text-slate-200'}`} data-id="element-6459">
                            {value}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500" data-id="element-6460">{desc}</p>
                      </button>)}
                  </div>
                </div>

                <div data-id="element-6461">
                  <label className="block text-sm font-medium text-slate-300 mb-3" data-id="element-6462">
                    Idioma Principal
                  </label>
                  <div className="grid grid-cols-3 gap-3" data-id="element-6463">
                    {[{
                  value: 'Spanish',
                  flag: '🇪🇸',
                  label: 'Español'
                }, {
                  value: 'English',
                  flag: '🇺🇸',
                  label: 'English'
                }, {
                  value: 'Portuguese',
                  flag: '🇧🇷',
                  label: 'Português'
                }].map(({
                  value,
                  flag,
                  label
                }) => <button key={value} onClick={() => updateForm({
                  agentLanguage: value
                })} className={`p-4 rounded-xl border text-center transition-all ${form.agentLanguage === value ? 'bg-indigo-900/30 border-indigo-500 text-indigo-300' : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700'}`} data-id="element-6464">
                        <span className="text-2xl block mb-1" data-id="element-6465">{flag}</span>
                        <span className="text-sm font-medium" data-id="element-6466">{label}</span>
                      </button>)}
                  </div>
                </div>

                {/* Agent Preview */}
                <div className="bg-slate-900 rounded-xl p-5 border border-slate-800" data-id="element-6467">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 font-semibold" data-id="element-6468">
                    Así se presentará tu agente
                  </p>
                  <div className="flex items-start gap-3" data-id="element-6469">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center text-white shrink-0" data-id="element-6470">
                      <Bot size={20} data-id="element-6471" />
                    </div>
                    <div className="bg-slate-800 rounded-2xl rounded-tl-none px-4 py-3 border border-slate-700" data-id="element-6472">
                      <p className="text-sm text-slate-200" data-id="element-6473">
                        {form.agentTone === 'Professional' && `Buenos días. Soy ${form.name}, tu asistente de gestión ágil. ¿En qué puedo ayudarte?`}
                        {form.agentTone === 'Friendly' && `¡Hola! 👋 Soy ${form.name}, estoy aquí para ayudarte con todo lo que necesites del proyecto. ¿Qué hacemos hoy?`}
                        {form.agentTone === 'Enthusiastic' && `¡Hey! 🚀 Soy ${form.name} y estoy listo para hacer que este sprint sea INCREÍBLE. ¡Vamos a por ello!`}
                        {form.agentTone === 'Strict' && `Soy ${form.name}. Revisemos el estado del sprint. Hay ${3} tareas pendientes que requieren atención inmediata.`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>}

          {/* STEP 5: Board Types */}
          {currentStep === 5 && <div className="space-y-8 animate-in fade-in slide-in-from-right-10 duration-300" data-id="element-6474">
              <div data-id="element-6475">
                <h2 className="text-2xl font-bold text-slate-100 mb-2" data-id="element-6476">
                  📊 Tipos de Tableros
                </h2>
                <p className="text-slate-400" data-id="element-6477">
                  Selecciona y configura los tableros que necesita tu equipo.
                </p>
              </div>
              <div className="space-y-3" data-id="element-6478">
                {form.boardTypes.map(board => <div key={board.id} className={`flex items-center justify-between p-4 rounded-xl border transition-all ${board.enabled ? 'bg-slate-900 border-indigo-800/50' : 'bg-slate-900/50 border-slate-800'}`} data-id="element-6479">
                    <div className="flex items-center gap-3" data-id="element-6480">
                      <span className="text-2xl" data-id="element-6481">{board.icon}</span>
                      <div data-id="element-6482">
                        <p className={`text-sm font-semibold ${board.enabled ? 'text-slate-200' : 'text-slate-500'}`} data-id="element-6483">
                          {board.name}
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-6484">
                          {board.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-6485">
                      {board.type === 'custom' && <button onClick={() => removeBoard(board.id)} className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-slate-800 transition-colors" data-id="element-6486">
                          <X size={14} data-id="element-6487" />
                        </button>}
                      <button onClick={() => toggleBoard(board.id)} className={`relative w-12 h-6 rounded-full transition-colors ${board.enabled ? 'bg-indigo-600' : 'bg-slate-700'}`} data-id="element-6488">
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${board.enabled ? 'translate-x-6' : 'translate-x-0'}`} data-id="element-6489" />
                      </button>
                    </div>
                  </div>)}

                {/* Add Custom Board */}
                {showAddBoard ? <div className="p-4 rounded-xl border border-dashed border-indigo-500/50 bg-indigo-950/20 space-y-3" data-id="element-6490">
                    <div className="grid grid-cols-2 gap-3" data-id="element-6491">
                      <input className="rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500" value={customBoardName} onChange={e => setCustomBoardName(e.target.value)} placeholder="Nombre del tablero" data-id="element-6492" />

                      <input className="rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500" value={customBoardDesc} onChange={e => setCustomBoardDesc(e.target.value)} placeholder="Descripción" data-id="element-6493" />
                    </div>
                    <div className="flex gap-2" data-id="element-6494">
                      <Button size="sm" onClick={addCustomBoard} className="gap-1" data-id="element-6495">
                        <Plus size={14} data-id="element-6496" /> Agregar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setShowAddBoard(false)} data-id="element-6497">
                        Cancelar
                      </Button>
                    </div>
                  </div> : <button onClick={() => setShowAddBoard(true)} className="w-full p-4 rounded-xl border border-dashed border-slate-700 text-slate-500 hover:border-indigo-500/50 hover:text-indigo-400 transition-all flex items-center justify-center gap-2 text-sm" data-id="element-6498">
                    <Plus size={16} data-id="element-6499" /> Agregar Tablero Personalizado
                  </button>}
              </div>
            </div>}

          {/* STEP 6: Review */}
          {currentStep === 6 && <div className="space-y-8 animate-in fade-in slide-in-from-right-10 duration-300" data-id="element-6500">
              <div className="text-center" data-id="element-6501">
                <div className="w-20 h-20 bg-indigo-600 rounded-2xl flex items-center justify-center text-white font-bold text-3xl shadow-lg shadow-indigo-900/30 mx-auto mb-4" data-id="element-6502">
                  {form.logoInitial}
                </div>
                <h2 className="text-2xl font-bold text-slate-100 mb-2" data-id="element-6503">
                  ¡Todo listo! 🎉
                </h2>
                <p className="text-slate-400" data-id="element-6504">
                  Revisa la configuración antes de lanzar.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-6505">
                <ReviewCard title="Empresa" items={[{
              label: 'Empresa',
              value: form.company
            }, {
              label: 'Producto',
              value: form.name
            }, {
              label: 'Industria',
              value: form.industry
            }]} data-id="element-6506" />

                <ReviewCard title="Equipo" items={[{
              label: 'Equipo',
              value: form.teamName
            }, {
              label: 'Tamaño',
              value: form.teamSize
            }]} data-id="element-6507" />

                <ReviewCard title="Proyecto" items={[{
              label: 'Clave',
              value: form.projectKey
            }, {
              label: 'Metodología',
              value: form.methodology
            }, {
              label: 'Sprint',
              value: `${form.sprintDuration} semanas`
            }]} data-id="element-6508" />

                <ReviewCard title="Agente" items={[{
              label: 'Tono',
              value: form.agentTone
            }, {
              label: 'Idioma',
              value: form.agentLanguage
            }]} data-id="element-6509" />
              </div>

              <div className="bg-slate-900 rounded-xl p-4 border border-slate-800" data-id="element-6510">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 font-semibold" data-id="element-6511">
                  Integraciones
                </p>
                <div className="flex gap-3" data-id="element-6512">
                  {form.jiraConnected && <span className="px-3 py-1 bg-blue-950/50 text-blue-400 border border-blue-900/50 rounded-full text-xs font-medium" data-id="element-6513">
                      Jira ✓
                    </span>}
                  {form.confluenceConnected && <span className="px-3 py-1 bg-blue-950/50 text-blue-400 border border-blue-900/50 rounded-full text-xs font-medium" data-id="element-6514">
                      Confluence ✓
                    </span>}
                  {form.slackConnected && <span className="px-3 py-1 bg-purple-950/50 text-purple-400 border border-purple-900/50 rounded-full text-xs font-medium" data-id="element-6515">
                      Slack ✓
                    </span>}
                  {form.githubConnected && <span className="px-3 py-1 bg-slate-800 text-slate-300 border border-slate-700 rounded-full text-xs font-medium" data-id="element-6516">
                      GitHub ✓
                    </span>}
                  {!form.jiraConnected && !form.confluenceConnected && !form.slackConnected && !form.githubConnected && <span className="text-xs text-slate-500" data-id="element-6517">
                        Ninguna integración conectada
                      </span>}
                </div>
              </div>

              <div className="bg-slate-900 rounded-xl p-4 border border-slate-800" data-id="element-6518">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 font-semibold" data-id="element-6519">
                  Tableros Activos
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-6520">
                  {form.boardTypes.filter(b => b.enabled).map(b => <span key={b.id} className="px-3 py-1.5 bg-indigo-950/50 text-indigo-300 border border-indigo-900/50 rounded-lg text-xs font-medium flex items-center gap-1.5" data-id="element-6521">
                        {b.icon} {b.name}
                      </span>)}
                </div>
              </div>
            </div>}
        </div>
      </div>

      {/* Footer Navigation */}
      <div className="h-20 bg-slate-900 border-t border-slate-800 flex items-center justify-between px-8 shrink-0" data-id="element-6522">
        <div data-id="element-6523">
          {currentStep > (mode === 'boards' ? 5 : 0) && <Button variant="outline" onClick={handleBack} className="gap-2" data-id="element-6524">
              <ChevronLeft size={16} data-id="element-6525" /> Anterior
            </Button>}
        </div>
        <div className="flex items-center gap-3" data-id="element-6526">
          <span className="text-xs text-slate-500" data-id="element-6527">
            Paso {displayStepIndex + 1} de{' '}
            {mode === 'boards' ? 2 : STEPS.length}
          </span>
          {currentStep === 6 || mode === 'boards' && currentStep === 6 ? <Button onClick={handleComplete} className="gap-2 bg-emerald-600 hover:bg-emerald-500" data-id="element-6528">
              <Rocket size={16} data-id="element-6529" /> Lanzar {form.name}
            </Button> : <Button onClick={handleNext} className="gap-2" data-id="element-6530">
              Siguiente <ChevronRight size={16} data-id="element-6531" />
            </Button>}
        </div>
      </div>
    </div>;
}
// Helper Components
function FormField({
  label,
  value,
  onChange,
  placeholder,
  hint,
  maxLength
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
  maxLength?: number;
}) {
  return <div data-id="element-6532">
      <label className="block text-sm font-medium text-slate-300 mb-1.5" data-id="element-6533">
        {label}
      </label>
      <input className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} maxLength={maxLength} data-id="element-6534" />

      {hint && <p className="text-xs text-slate-500 mt-1" data-id="element-6535">{hint}</p>}
    </div>;
}
function ReviewCard({
  title,
  items
}: {
  title: string;
  items: {
    label: string;
    value: string;
  }[];
}) {
  return <div className="bg-slate-900 rounded-xl p-4 border border-slate-800" data-id="element-6536">
      <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 font-semibold" data-id="element-6537">
        {title}
      </p>
      <div className="space-y-2" data-id="element-6538">
        {items.map(({
        label,
        value
      }) => <div key={label} className="flex justify-between text-sm" data-id="element-6539">
            <span className="text-slate-500" data-id="element-6540">{label}</span>
            <span className="text-slate-200 font-medium" data-id="element-6541">{value}</span>
          </div>)}
      </div>
    </div>;
}